/**
 * The default print theme.
 *
 * Every visual decision the old React templates carried inline lives
 * here instead — one stylesheet for all documents, versioned
 * independently of both the IR and any block. Re-theming is a CSS change;
 * it cannot alter what a document says.
 *
 * v2 reproduces the legacy template lane's look (the Tailwind atoms in
 * `@hyranai/regta-templates`): Georgia body text at 11px / 10.5px, ruled
 * section headings, run-in clause titles, dotted label/value rows, square
 * tick boxes and the measured signature panel. Pixel values are the
 * Tailwind ones they replace (e.g. `mt-4` = 16px, stone-300 = #d6d3d1).
 */
export const THEME_VERSION = '2';
const SERIF = `Georgia,'Times New Roman',serif`;
const SANS = `ui-sans-serif,system-ui,sans-serif,'Apple Color Emoji','Segoe UI Emoji','Segoe UI Symbol','Noto Color Emoji'`;
export const defaultThemeCss = `
.rd-doc{font-family:${SERIF};font-size:11px;line-height:1.625;color:#292524;margin:0;
  -webkit-print-color-adjust:exact;print-color-adjust:exact}
.rd-doc *,.rd-doc *::before,.rd-doc *::after{box-sizing:border-box}
.rd-part{page-break-after:always}
.rd-part:last-child{page-break-after:auto}

/* Headings */
.rd-heading{margin:0 0 6px;font-weight:700;line-height:1.3}
h1.rd-heading{font-size:13px;font-weight:600;letter-spacing:.025em;text-transform:uppercase;margin-bottom:4px}
h2.rd-heading{font-size:12px}
h3.rd-heading{font-size:11px}
.rd-heading--center{text-align:center}

/* Paragraphs: clause text is 10.5px relaxed, as in every legacy template. */
.rd-p{margin:0 0 8px;font-size:10.5px;line-height:1.625}
.rd-p:last-child{margin-bottom:0}
.rd-p--lead{font-weight:600}
/* Opening line ("Undertecknande parter har denna dag …"): italic, at body size. */
.rd-p--preamble{font-style:italic;color:#57534e;margin-bottom:12px;font-size:10.5px;line-height:1.625}
.rd-p--intro{font-style:italic;color:#57534e;margin-bottom:12px;font-size:10.5px;line-height:1.625}
.rd-p--fine_print{color:#44403c}
.rd-p--caption{font-family:${SANS};font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:#78716c;margin:8px 0 4px}
.rd-p--center{text-align:center}
.rd-p--right{text-align:right}
/* Run-in field label ("AVTALSNUMMER:"): small sans caps, stone-500. */
.rd-label{font-family:${SANS};text-transform:uppercase;letter-spacing:.1em;color:#78716c}

/* Sections. Top level: ruled 12px bold heading, number in stone-600. */
.rd-section{margin:16px 0 8px;break-inside:avoid}
.rd-section__heading{display:flex;gap:8px;align-items:baseline;margin:6px 0 6px;padding-bottom:4px;
  border-bottom:1px solid #d6d3d1;font-size:12px;font-weight:700;line-height:1.3}
.rd-section__number{min-width:1.6em;color:#57534e}
/* Point-numbered documents: "4. Title" run in, ruled, no gutter (legacy FO Heading). */
.rd-section--run-in{margin-bottom:0}
.rd-section__heading--run-in{display:block;margin-top:0}
.rd-section__heading--run-in>.rd-section__number{min-width:0;color:inherit;margin-right:.25em}
.rd-section__body{font-size:11px;line-height:1.625}
/* Nested sections: small caps sub-clause line "5.2 TITLE" (legacy SubClause). */
.rd-section .rd-section:not([data-variant]){margin:12px 0 0}
.rd-section .rd-section:not([data-variant])>.rd-section__heading{display:block;border-bottom:0;padding-bottom:0;margin:0 0 2px;
  font-family:${SANS};font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.08em;color:#57534e}
.rd-section .rd-section:not([data-variant])>.rd-section__heading>.rd-section__number{min-width:0;color:inherit;margin-right:.3em}
/* Clause: run-in bold "6. Title" over its text (legacy ClauseBlock). */
.rd-section[data-variant=clause]{margin:12px 0}
.rd-section[data-variant=clause]>.rd-section__heading{display:block;border-bottom:0;padding-bottom:0;margin:0 0 4px;font-size:11px}
.rd-section[data-variant=clause]>.rd-section__heading>.rd-section__number{min-width:0;color:inherit;margin-right:.3em}
.rd-section[data-variant=clause]>.rd-section__body>.rd-p{white-space:pre-wrap}

/* Values: a filled value sits on a light rule; multi-line values (lists of
   fastigheter, addresses) keep their line breaks and drop the rule. */
.rd-value{font-family:${SERIF};border-bottom:1px solid #d6d3d1;font-variant-numeric:tabular-nums}
.rd-value{white-space:pre-wrap}
.rd-value[data-format="checkbox"]{font-family:inherit;border-bottom:0}
.rd-value--multiline{display:inline-block;white-space:pre-wrap;border-bottom:0;vertical-align:top}
.rd-details .rd-value,.rd-table .rd-value,.rd-signature .rd-value{border-bottom:0}
/* Blanks print the legacy underscore run; a full-width one is clipped to the line. */
.rd-blank{white-space:nowrap;letter-spacing:0}
.rd-blank--full{display:block;overflow:hidden}
.rd-blank--rule{display:inline-block;border-bottom:1px solid #a8a29e;vertical-align:baseline;height:1em}
.rd-blank--rule.rd-blank--short{width:6em}.rd-blank--rule.rd-blank--medium{width:12em}.rd-blank--rule.rd-blank--long{width:20em}.rd-blank--rule.rd-blank--full{width:100%}

.rd-list{margin:4px 0 8px;padding-left:20px;font-size:10.5px;line-height:1.625}
ol.rd-list{list-style:decimal}
ul.rd-list{list-style:disc}
.rd-list__item{margin:2px 0 0}
.rd-list__item>.rd-p{margin:0}

/* Tick boxes: a 14px square; ticked is filled stone-800 with a white tick
   (legacy Checkbox atom). The glyph stays in the text layer, sized away. */
.rd-options{list-style:none;margin:0 0 8px;padding:0;font-size:10.5px;line-height:1.625}
.rd-option{display:flex;flex-wrap:wrap;gap:0 6px;align-items:flex-start;margin:0 0 4px}
.rd-option__box{position:relative;flex:none;display:inline-block;width:14px;height:14px;margin-top:3px;
  border:1px solid #78716c;border-radius:2px;font-size:0;line-height:0;color:transparent}
.rd-option--checked>.rd-option__box{background:#292524;border-color:#292524}
.rd-option--checked>.rd-option__box::after{content:'\\2713';position:absolute;inset:0;display:flex;
  align-items:center;justify-content:center;font-size:10px;line-height:1;font-weight:700;color:#fff}
.rd-option__label{flex:1 1 0;min-width:0}
.rd-option__children{flex-basis:100%;padding-left:20px;margin-top:2px}
/* Inline options run on in one line, each box hugging its label. */
.rd-options--inline{display:flex;flex-wrap:wrap;gap:2px 12px}
.rd-options--inline>.rd-option{flex-wrap:nowrap;gap:0 4px;margin:0}
.rd-options--inline .rd-option__label{flex:0 1 auto}

/* Tables */
.rd-table{width:100%;border-collapse:collapse;margin:0 0 8px;font-size:10px;line-height:1.4}
.rd-table__th,.rd-table__td{border:1px solid #a8a29e;padding:2px 4px;text-align:left;vertical-align:top}
.rd-table__th{font-weight:600;background:#f5f5f4}
.rd-table__td[data-align=right],.rd-table__th[data-align=right]{text-align:right}
.rd-table__td[data-align=center],.rd-table__th[data-align=center]{text-align:center}
.rd-table__tr--total .rd-table__td{font-weight:700;background:#fafaf9}
.rd-table--details .rd-table__th,.rd-table--details .rd-table__td{border:0;padding:4px 8px 4px 0;font-size:10.5px}
.rd-table--details .rd-table__td:first-child{font-weight:600}
.rd-table--plain .rd-table__th,.rd-table--plain .rd-table__td{border:0;padding:2px 4px;background:none}
.rd-table__caption{caption-side:top;text-align:left;font-family:${SANS};font-size:9px;color:#78716c;padding-bottom:3px}
.rd-table .rd-p{margin:0;font-size:inherit}

/* Label/value rows (legacy FieldRow): 40/60 grid, dotted rule. */
.rd-details{margin:0 0 8px}
.rd-details__row{display:grid;grid-template-columns:40% 60%;gap:8px;padding:4px 0;border-bottom:1px dotted #e7e5e4}
.rd-details__term{min-width:0;font-family:${SANS};font-size:10px;line-height:1.5;color:#57534e;overflow-wrap:anywhere}
.rd-details__value{margin:0;min-width:0;font-size:11px;line-height:1.625;overflow-wrap:anywhere}
.rd-details__value .rd-p{margin:0;font-size:11px}
.rd-details--boxed{border:1px solid #d6d3d1;padding:0 8px}
.rd-details--boxed .rd-details__row:last-child{border-bottom:0}
/* Paired rows (legacy grid-cols-2 gap-6 of FieldRows): items flow row by row. */
.rd-details--cols-2{display:grid;grid-template-columns:1fr 1fr;column-gap:24px}
.rd-details--cols-2>.rd-details__row{min-width:0}

/* Side-by-side siblings (LayoutHints.sideBySide), legacy grid-cols-2 gap-6. */
.rd-row{display:grid;grid-template-columns:repeat(var(--rd-cols,2),minmax(0,1fr));gap:24px;break-inside:avoid}
.rd-row--3{--rd-cols:3}.rd-row--4{--rd-cols:4}
.rd-row__cell{min-width:0}
.rd-row__cell>.rd-section{margin-bottom:8px}

/* Groups */
.rd-group{margin:0 0 8px}
.rd-group--boxed{border:1px solid #d6d3d1;margin:12px 0}
.rd-group--boxed>.rd-group__title{background:#f5f5f4;margin:0;padding:3px 8px;font-family:${SANS};font-size:10px;
  text-transform:uppercase;letter-spacing:.1em;color:#78716c}
.rd-group--boxed>:not(.rd-group__title){margin-left:8px;margin-right:8px}
.rd-group--indent{padding-left:24px}
.rd-group__title{font-family:${SANS};font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:#78716c;margin:8px 0 4px}
/* Side-by-side columns (legacy grid-cols-2 gap-6). */
.rd-group__columns{display:grid;grid-template-columns:1fr 1fr;gap:24px}
.rd-group__column{min-width:0}
.rd-group__column>.rd-section{margin-top:0}

.rd-notice{margin:12px 0;padding:6px 8px;border:1px solid #d6d3d1;background:#fafaf9;font-size:10.5px}
.rd-notice--warning{border-color:#b45309}
.rd-notice--legal{border-color:#292524}
.rd-notice__title{margin:0 0 4px;font-weight:700;font-size:11px}

/* Signatures (legacy SigningBlock). Panel rows are the measured geometry:
   a 34px signature line, then optional Namnförtydligande / Ort / Datum
   blanks 14px high. Measurement markers sit at each line's bottom-left. */
.rd-signature{margin-top:40px;break-inside:avoid}
.rd-signature__statement{margin:0 0 20px;font-size:10.5px;line-height:1.625;white-space:pre-wrap}
.rd-signature__heading{margin:0 0 12px;font-size:11px;font-weight:700}
.rd-signature__panels{display:grid;gap:32px 32px;grid-template-columns:1fr 1fr}
.rd-signature--cols-1 .rd-signature__panels{grid-template-columns:1fr}
.rd-signature__panel{font-size:10px;line-height:1.5;color:#44403c;break-inside:avoid}
.rd-signature__main{margin-bottom:24px}
.rd-signature__line{position:relative;height:34px;border-bottom:1px solid #57534e}
.rd-signature__caption{margin:4px 0 0;font-family:${SANS};font-size:8.5px;color:#78716c}
.rd-signature__name{margin:4px 0 0;font-size:10px;color:#292524}
.rd-signature__legal-id,.rd-signature__entity{margin:0;font-size:10px;color:#44403c}
.rd-signature__capacity{margin:2px 0 0;font-size:9px;font-style:italic;color:#57534e}
.rd-signature__row{display:grid;grid-template-columns:auto 1fr;align-items:end;column-gap:8px;margin-top:4px}
.rd-signature__row-label{white-space:nowrap;color:#57534e}
.rd-signature__blank{position:relative;display:block;height:14px;border-bottom:1px solid #a8a29e}

.rd-attachments{list-style:none;margin:0 0 8px;padding:0;font-size:10.5px}
.rd-attachments__item{display:flex;gap:8px;padding:2px 0}
.rd-attachments__number{min-width:5em;font-weight:600}

.rd-spacer--sm{height:8px}.rd-spacer--md{height:16px}.rd-spacer--lg{height:24px}
.rd-divider{border:0;border-top:1px solid #d6d3d1;margin:12px 0}
.rd-page-break{page-break-after:always;break-after:page}

/* Letterhead (Gotenberg header strip): owner mark with the title (12px
   semibold) and form number (9px sans caps, tracked) stacked under it on
   the left, customer mark on the right. Last in the sheet so the region
   rules win over the generic table/paragraph ones. */
/* Chromium lays the print header/footer body out as a flex row, which
   shrink-wraps the region; growing it is what lets widths reach the page. */
.rd-letterhead,.rd-region--letterhead,.rd-footer,.rd-region--footer{flex:1 1 auto}
.rd-letterhead,.rd-region--letterhead{font-family:${SERIF};font-size:11px;line-height:1.25;color:#292524}
.rd-image{display:inline-block;vertical-align:top;object-fit:contain}
.rd-letterhead .rd-table,.rd-region--letterhead .rd-table{margin:0;table-layout:fixed;width:100%;border-collapse:collapse}
.rd-letterhead .rd-table .rd-table__td,.rd-region--letterhead .rd-table .rd-table__td{padding:0;border:0;vertical-align:top}
.rd-letterhead .rd-p,.rd-letterhead .rd-table .rd-p,.rd-region--letterhead .rd-p,.rd-region--letterhead .rd-table .rd-p{margin:0;font-size:11px;line-height:1.25}
.rd-letterhead .rd-p:has(>.rd-image:only-child),.rd-region--letterhead .rd-p:has(>.rd-image:only-child){line-height:0}
.rd-letterhead .rd-p--lead,.rd-letterhead .rd-table .rd-p--lead,.rd-region--letterhead .rd-p--lead,.rd-region--letterhead .rd-table .rd-p--lead{font-size:12px;font-weight:600;line-height:1.25;
  margin-top:12px;font-style:normal;color:#292524}
.rd-letterhead .rd-p--caption,.rd-letterhead .rd-table .rd-p--caption,.rd-region--letterhead .rd-p--caption,.rd-region--letterhead .rd-table .rd-p--caption{font-family:${SANS};font-size:9px;line-height:1.33;
  text-transform:uppercase;letter-spacing:.15em;color:#78716c;margin-top:2px}

/* Page footer (Gotenberg footer strip): 9px sans, stone-500. */
.rd-footer,.rd-region--footer{font-family:${SANS};font-size:9px;line-height:1.4;color:#78716c}
.rd-footer .rd-p,.rd-footer .rd-table .rd-p,.rd-region--footer .rd-p,.rd-region--footer .rd-table .rd-p{margin:0;font-size:9px;font-family:${SANS};color:#78716c;
  text-transform:none;letter-spacing:0}
.rd-footer .rd-table,.rd-region--footer .rd-table{margin:0;width:100%;border-collapse:collapse;font-size:9px}
.rd-footer .rd-table .rd-table__td,.rd-region--footer .rd-table .rd-table__td{padding:0;border:0}

[data-keep-together=true]{break-inside:avoid}
[data-keep-with-next=true]{break-after:avoid}
[data-page-break-before=true]{break-before:page}
`.trim();
/**
 * Screen-only additions for an on-screen preview: the white A4 sheet with
 * the letterhead and footer inline (legacy TemplateDocument). Not for the
 * PDF, where Gotenberg places the header/footer strips itself.
 */
export const previewThemeCss = `
.rd-page{box-sizing:border-box;width:21cm;min-height:29.7cm;margin:0 auto 24px;padding:2cm 2.2cm 2.5cm;background:#fff;
  box-shadow:0 10px 15px -3px rgba(214,211,209,.5),0 4px 6px -4px rgba(214,211,209,.5)}
.rd-page>.rd-region--letterhead{margin-bottom:16px;padding-bottom:6px;border-bottom:1px solid #d6d3d1}
.rd-page>.rd-region--footer{margin-top:24px;padding-top:4px;border-top:1px solid #d6d3d1}
@media print{.rd-page{box-shadow:none;margin:0;width:auto;min-height:0;padding:0}}
`.trim();
/** The two stylesheets a self-contained preview needs. */
export const previewCss = `${defaultThemeCss}\n${previewThemeCss}`;
//# sourceMappingURL=theme.js.map